import { ref, computed } from 'vue'
import type { FormValidatorFunc, EnableCheckFlg } from '@/types/form'
import { createEnableCheckFlg } from '@/utils/form'
import { getValues, getKeys } from '@/utils/object'

export const useForm = <T extends object>(defaultFields: T, validator: FormValidatorFunc<T>) => {
  const fields = ref({ ...defaultFields })
  const enableCheckFlg = ref(createEnableCheckFlg(defaultFields))

  const formErrors = computed(() => {
    const errors = validator(fields.value as T, enableCheckFlg.value as EnableCheckFlg<T>)
    return errors
  })

  const hasFormErrors = computed(() => {
    const allErrors = getValues(formErrors.value)
    return allErrors.some((f) => f.length !== 0)
  })

  const enableCheck = (key: keyof T) => {
    enableCheckFlg.value = {
      ...enableCheckFlg.value as any,
      [key]: true
    }
  }

  const disableCheck = (key: keyof T) => {
    enableCheckFlg.value = {
      ...enableCheckFlg.value as any,
      [key]: false,
    }
  }

  const enableAllChecks = () => {
    for (const field of getKeys(defaultFields)) {
      enableCheck(field)
    }
  }

  const disableAllChecks = () => {
    for (const field of getKeys(defaultFields)) {
      disableCheck(field)
    }
  }

  const clearValue = () => {
    fields.value = { ...defaultFields } as any
    disableAllChecks()
  }

  return {
    fields,
    enableCheckFlg,
    enableAllChecks,
    formErrors,
    hasFormErrors,
    enableCheck,
    disableCheck,
    disableAllChecks,
    clearValue,
  }
}
